import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { CheckRounded, ErrorOutlineOutlined, FileUploadOutlined, ListAltOutlined, RemoveRounded } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { pfMemberHeader } from "pages/PayRoll/PayRollHeaders";
import { ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { StatCardGrid, StatutoryList, StatutoryPage, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { MEMBER_STATUS_COLORS } from "./pfMembersData";
import { endPfOptOut, fetchPfMember, fetchPfMembers, savePfExtra, savePfOptOut } from "./pfMembersApi";
import { PfExtraPanel, PfMemberDetailsPanel, PfOptOutPanel } from "./PfMemberPanels";
import { PfUanImport } from "./PfUanImport";
import { OpenSettingsButton } from "pages/PayRoll/Shared/settingsSwitch";

// Payroll > Statutory > PF > Members (screens P4–P6b): who is in PF, their
// UAN, pension (EPS) and any extra PF. Search, card filter and paging run
// on the server (PF Members APIs). Monthly PF amounts are in Reports.

const ALL = "all";
const RED = "#DC2626";
const SEARCH_DELAY = 300;
const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 500;
const PF_SETTINGS_ROUTE = "/admin/payroll/configuration/statutory-configuration?item=pf";
const STALE_MESSAGE = "Someone else changed this employee's PF details. Reloading…";

export const StatutoryPF = () => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh data per company.
  return <PfMembersScreen key={`${domain}:${topbarCompanyId}`} domain={domain} companyId={topbarCompanyId} />;
};

const PfMembersScreen = ({ domain, companyId }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const history = useHistory();
  const [card, setCard] = useState(ALL);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [reloadKey, setReloadKey] = useState(0);
  // settings: undefined until loaded, null = PF not set up.
  const [list, setList] = useState({ loading: true, error: "", rows: [], count: 0, cards: {}, settings: undefined });
  const [viewId, setViewId] = useState(null);
  const [details, setDetails] = useState({ loading: false, error: "", member: null });
  const [detailsKey, setDetailsKey] = useState(0);
  const [extraOpen, setExtraOpen] = useState(false);
  const [optOutOpen, setOptOutOpen] = useState(false);
  const [endOptOut, setEndOptOut] = useState(null);
  const [ending, setEnding] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const firstSearch = useRef(true);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "pf_members_saved_views",
    fullHeaders: pfMemberHeader,
  });

  // Search waits for typing to pause, then starts again from page 1.
  useEffect(() => {
    if (firstSearch.current) {
      firstSearch.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    setList((current) => ({ ...current, loading: true, error: "" }));
    fetchPfMembers({ domain, companyId, search: query, card, page: page + 1, pageSize, signal: controller.signal })
      .then((data) => setList({ loading: false, error: "", rows: data.rows, count: data.count, cards: data.cards, settings: data.settings }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setList((current) => ({ ...current, loading: false, error: error.message || "Could not load PF members." }));
      });
    return () => controller.abort();
  }, [domain, companyId, query, card, page, pageSize, reloadKey]);

  useEffect(() => {
    if (viewId == null) {
      setDetails({ loading: false, error: "", member: null });
      return undefined;
    }
    const controller = new AbortController();
    setDetails((current) => ({ loading: true, error: "", member: current.member?.id === viewId ? current.member : null }));
    fetchPfMember({ domain, companyId, employeeId: viewId, signal: controller.signal })
      .then((member) => setDetails({ loading: false, error: "", member }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setDetails({ loading: false, error: error.message || "Could not load the PF details.", member: null });
      });
    return () => controller.abort();
  }, [domain, companyId, viewId, detailsKey]);

  const reloadList = () => setReloadKey((key) => key + 1);
  const reloadDetails = () => setDetailsKey((key) => key + 1);

  const changeCard = (key) => {
    setCard(key);
    setPage(0);
  };

  // A save returns the member's fresh details; the list page is fetched again.
  const afterSave = ({ member, message }) => {
    setDetails({ loading: false, error: "", member });
    reloadList();
    showToast("success", message);
  };
  const handleStale = (error) => {
    if (error?.code !== "STALE_VERSION") return false;
    showToast("error", STALE_MESSAGE);
    setExtraOpen(false);
    setOptOutOpen(false);
    reloadDetails();
    reloadList();
    return true;
  };

  // The panels show field errors from a rejected save.
  const saveExtra = async (form) => {
    try {
      const result = await savePfExtra({ domain, companyId, employeeId: details.member.id, form, rowVersion: details.member.row_version });
      setExtraOpen(false);
      afterSave(result);
    } catch (error) {
      if (!handleStale(error)) throw error;
    }
  };
  const saveOptOut = async (form) => {
    try {
      const result = await savePfOptOut({ domain, companyId, employeeId: details.member.id, form, rowVersion: details.member.row_version });
      setOptOutOpen(false);
      afterSave(result);
    } catch (error) {
      if (!handleStale(error)) throw error;
    }
  };
  const confirmEndOptOut = async () => {
    if (ending || !endOptOut) return;
    setEnding(true);
    try {
      const result = await endPfOptOut({ domain, companyId, employeeId: endOptOut.id, rowVersion: endOptOut.row_version });
      setEndOptOut(null);
      afterSave(result);
    } catch (error) {
      setEndOptOut(null);
      if (!handleStale(error)) showToast("error", error.message || "Could not end the opt-out.");
    } finally {
      setEnding(false);
    }
  };

  // The import panel stays open on its result until Done.
  const afterImport = (message) => {
    reloadList();
    if (viewId != null) reloadDetails();
    showToast("success", message);
  };

  // Export: every matching row, page by page.
  const exportRows = useCallback(async () => {
    const all = [];
    for (let next = 1; ; next += 1) {
      // eslint-disable-next-line no-await-in-loop
      const data = await fetchPfMembers({ domain, companyId, search: query, card, page: next, pageSize: EXPORT_PAGE_SIZE });
      all.push(...data.rows);
      if (!data.rows.length || all.length >= data.count) return all;
    }
  }, [domain, companyId, query, card]);

  const cardValue = (key) => Number(list.cards?.[key]) || 0;
  const cards = [
    { key: ALL, title: "All", value: cardValue("all"), icon: ListAltOutlined, tone: "blue" },
    { key: "members", title: "PF members", value: cardValue("members"), icon: CheckRounded, tone: "green" },
    { key: "opted_out", title: "Opted out", value: cardValue("opted_out"), icon: RemoveRounded, tone: "amber" },
    { key: "needs_fix", title: "UAN / DOB missing", value: cardValue("needs_fix"), icon: ErrorOutlineOutlined, tone: "red" },
  ];

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>
              {initialsOf(row.name)}
            </Mui.Avatar>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        );
      case "uan_label":
        return row.uan ? row.uan : <Mui.Box component="span" sx={{ color: row.status === "Member" ? "#F97316" : undefined }}>{row.uan_label}</Mui.Box>;
      case "dob_label":
        return row.dob ? row.dob_label : <Mui.Box component="span" sx={{ color: RED }}>Missing</Mui.Box>;
      case "pf_wage":
        return Number(row.pf_wage).toLocaleString("en-IN");
      case "status":
        return <RequestStatusChip status={row.status} colorCode={MEMBER_STATUS_COLORS[row.status]} />;
      case "pension_label":
        return row.pension_tone ? <ToneChip label={row.pension_label} tone={row.pension_tone} /> : "—";
      case "extra_label":
        return row.extra_tone ? <ToneChip label={row.extra_label} tone={row.extra_tone} /> : "—";
      default:
        return row[key] || "—";
    }
  };

  const notSetUp = list.settings === null;
  // List settings first; the member details response carries them too.
  const settings = list.settings || details.member?.settings || null;
  // The drawer's actions follow the PF Status the list shows for the row
  // that was clicked; the details call is only the fallback.
  const listRow = list.rows?.find((row) => row.id === viewId);
  const viewMember = details.member && listRow?.status ? { ...details.member, status: listRow.status } : details.member;

  return (
    <StatutoryPage
      breadcrumb={["Payroll", "Statutory", "PF", "Members"]}
      title="PF Members"
      switchAction={<OpenSettingsButton group="statutory" item="pf" />}
      subtitle="Who is in PF, their UAN, pension (EPS) and any extra PF they pay. Monthly PF amounts are in Reports › Statutory Reports › PF."
    >
      {notSetUp ? (
        <Mui.Box sx={{ mb: 1.5 }}>
          <RequestInfoBanner
            tone="warning"
            body="PF is not set up yet. Go to Payroll › Payroll Settings › Statutory › PF to enter the PF settings."
            action={
              <SecondaryButton onClick={() => history?.push(PF_SETTINGS_ROUTE)} sx={{ whiteSpace: "nowrap" }}>
                Open PF settings
              </SecondaryButton>
            }
          />
        </Mui.Box>
      ) : null}
      {list.error ? (
        <Mui.Box sx={{ mb: 1.5 }}>
          <RequestInfoBanner
            tone="danger"
            title="Couldn't load the PF members."
            body={list.error}
            action={<SecondaryButton onClick={reloadList} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
          />
        </Mui.Box>
      ) : null}
      <StatCardGrid cards={cards} value={card} onChange={changeCard} />
      <StatutoryList
        title="PF Members"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={list.rows}
        loading={list.loading}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="employees"
        searchPlaceholder="Search name, ID or UAN"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
        server={{
          search,
          onSearchChange: setSearch,
          page,
          rowsPerPage: pageSize,
          count: list.count,
          onPageChange: setPage,
          onRowsPerPageChange: (size) => {
            setPageSize(size);
            setPage(0);
          },
          exportRows,
        }}
        // Import UAN doesn't use the PF settings, so HR can load UANs
        // before the first PF version starts.
        action={
          <SecondaryButton startIcon={<FileUploadOutlined />} onClick={() => setImportOpen(true)} sx={{ minHeight: 40, whiteSpace: "nowrap", flexShrink: 0 }}>
            Import UAN
          </SecondaryButton>
        }
      />

      <PfMemberDetailsPanel
        open={viewId != null && !extraOpen && !optOutOpen}
        loading={details.loading && !details.member}
        error={details.error}
        onRetry={reloadDetails}
        member={viewMember}
        settings={settings}
        domain={domain}
        onClose={() => setViewId(null)}
        onEditExtra={() => setExtraOpen(true)}
        onOptOut={() => setOptOutOpen(true)}
        onEndOptOut={setEndOptOut}
      />
      <PfExtraPanel open={extraOpen && Boolean(details.member)} member={details.member} settings={settings} onClose={() => setExtraOpen(false)} onSave={saveExtra} />
      <PfOptOutPanel
        open={optOutOpen && Boolean(details.member)}
        member={details.member}
        notice={
          !settings
            ? "PF settings are not loaded (Payroll › Payroll Settings › Statutory › PF), so Save may be refused."
            : !settings.optOutAllowed
              ? "Opt-out is turned off in PF Configuration › Employee Options, so Save will be refused until it is turned on."
              : ""
        }
        onClose={() => setOptOutOpen(false)}
        onSave={saveOptOut}
      />
      <PfUanImport
        open={importOpen}
        domain={domain}
        companyId={companyId}
        onClose={() => setImportOpen(false)}
        onImported={afterImport}
      />

      <GlobalConfirmDialog
        open={Boolean(endOptOut)}
        title="End opt-out"
        onCancel={() => (ending ? null : setEndOptOut(null))}
        onConfirm={confirmEndOptOut}
        confirmLabel="End Opt-out"
        cancelLabel="Keep"
        loading={ending}
        loadingLabel="Saving..."
        maxWidth="sm"
      >
        {endOptOut?.name} rejoins PF from {moment().add(1, "month").format("MMM YYYY")}. PF is deducted from that month&apos;s payroll and they are included in the ECR.
      </GlobalConfirmDialog>
    </StatutoryPage>
  );
};
